<template>
  <div class="d-flex align-center flex-column justify-center">
    <v-icon icon="$noTicketsL" size="80"></v-icon>
    <div class="headline6">
      {{
        $t("modals.allowCloudService.finalStep.title", {
          service: isOtherProvider
            ? modelValue.domain
            : $t("services." + modelValue.selectedService),
        })
      }}
    </div>
    <div class="caption">{{ $t("modals.allowCloudService.finalStep.subtitle") }}</div>
    <v-btn
      class="mt-6 mb-4"
      color="primary"
      :loading="loading"
      rounded
      @click="activateConfiguration"
    >
      {{ $t("modals.allowCloudService.finalStep.activate") }}
    </v-btn>
    <div
      class="coro-link body2"
      v-if="satConnections.length < MAX_SAT_CONNECTIONS"
      @click="reopenModal"
    >
      + {{ $t("modals.allowCloudService.finalStep.connectAnother") }}
    </div>
  </div>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref } from "vue";
import { Service } from "@/constants/cloud-apps.ts";
import {
  SatConfigurationStatus,
  SatLocale,
  SatServiceProvider,
  SatServiceProviderToServiceMap,
  SatStatusToStepMap,
  SecurityAwarenessAction,
} from "@/constants/security-awareness.ts";
import {
  MAX_SAT_CONNECTIONS,
  useSecurityAwarenessModule,
} from "@/_store/security-awareness/security-awareness.module.ts";
import { useSecurityAwarenessEmptyState } from "@/composables/useSecurityAwarenessEmptyState.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import type { SatConnection } from "@/_store/security-awareness/security-awareness.adapters.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/utils.ts";
import { ModalWidth } from "@/constants/modals.ts";
import SatLanguageSelectModal from "@/views/settings/security-awareness/SatLanguageSelectModal.vue";
import { storeToRefs } from "pinia";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<SatConnectionConfigForm>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["dismissed"],
  setup(props, { emit }) {
    const securityAwarenessStore = useSecurityAwarenessModule();
    const dialogsStore = useDialogsStore();
    const { satConnections } = storeToRefs(securityAwarenessStore);
    const { handleAllowButtonClick } = useSecurityAwarenessEmptyState();
    const loading = ref(false);

    const formValue = computed(() => props.modelValue);

    const isOtherProvider = computed(() => {
      return formValue.value.selectedService === "other";
    });

    const satConfigurationProvider = computed(
      () =>
        SatServiceProviderToServiceMap[formValue.value.selectedService as Service] ??
        SatServiceProvider.CUSTOM
    );

    const reopenModal = () => {
      emit("dismissed");

      handleAllowButtonClick();
    };

    const openLanguageSelectionModal = async () => {
      const activatedStep = SatStatusToStepMap[SatConfigurationStatus.ACTIVATED];
      const hasActivatedConnection = satConnections.value.some(
        (item) => item.step === activatedStep
      );

      if (hasActivatedConnection) {
        await securityAwarenessStore.updateSettings({
          emailProviderConfigurations: {
            [satConfigurationProvider.value]: {
              status: SatConfigurationStatus.ACTIVATED,
            },
          },
        });

        return;
      }

      const config = componentDialogsConfigConstructor<SatConnection[]>({
        width: ModalWidth.MEDIUM,
        action: SecurityAwarenessAction.SELECT_SAT_LANGUAGE,
        item: [],
        component: SatLanguageSelectModal,
        disable: props.disable,
        callback: async (value: { preferredLanguageCode: SatLocale }) => {
          await securityAwarenessStore.updateSettings({
            preferredLanguageCode: value?.preferredLanguageCode,
            emailProviderConfigurations: {
              [satConfigurationProvider.value]: {
                status: SatConfigurationStatus.ACTIVATED,
              },
            },
          });
        },
      });

      dialogsStore.openDialog(config);
    };

    const activateConfiguration = async () => {
      loading.value = true;
      await openLanguageSelectionModal();
      emit("dismissed");
      loading.value = false;
    };

    return {
      isOtherProvider,
      MAX_SAT_CONNECTIONS,
      satConnections,
      reopenModal,
      activateConfiguration,
      loading,
    };
  },
});
</script>
